import { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useUserSelector } from 'hooks/useUser';
import { isCountrySupported } from 'pages/country-services/config';

/**
 * Route guard that only allows access if:
 * 1. The user's property country is supported
 * 2. The URL country code matches the user's property country
 *
 * If URL doesn't match but user has a supported country, redirects to their country-services.
 */
export function CountryServiceRoute({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { countryCode: urlCountryCode } = useParams();
  const { user, isLoading } = useUserSelector();
  const userCountryCode = user?.passport_data?.property_country?.country_code?.toLowerCase();
  const isSupported = userCountryCode && isCountrySupported(userCountryCode);
  const urlMatchesUser = urlCountryCode?.toLowerCase() === userCountryCode;

  useEffect(() => {
    if (isLoading) return;

    if (!isSupported) {
      navigate('/', { replace: true });
    } else if (!urlMatchesUser) {
      // Redirect to the user's country-services URL, preserving any sub-path
      const subPath = location.pathname.replace(`/country-services/${urlCountryCode}`, '');
      navigate(`/country-services/${userCountryCode}${subPath}`, { replace: true });
    }
  }, [isLoading, isSupported, urlMatchesUser, userCountryCode, urlCountryCode, location.pathname, navigate]);

  if (isLoading || !isSupported || !urlMatchesUser) {
    return null;
  }

  return children;
}
